export const STROKE_ORDER = [
    'Freestyle',
    'Breaststroke',
    'Butterfly',
    'Backstroke',
    'Individual Medley',
];
const normalizeStroke = (stroke) => stroke.trim().replace(/\s+/g, ' ');
export const parseEventName = (event) => {
    const match = event.match(/^\s*(\d+)\s*(.+)$/);
    if (match) {
        return {
            distance: parseInt(match[1], 10),
            stroke: normalizeStroke(match[2]),
        };
    }
    return {
        distance: Number.POSITIVE_INFINITY,
        stroke: normalizeStroke(event),
    };
};
export const getStrokePriority = (stroke) => {
    const normalized = normalizeStroke(stroke).toLowerCase();
    const index = STROKE_ORDER.findIndex(order => order.toLowerCase() === normalized);
    return index !== -1 ? index : STROKE_ORDER.length;
};
export const getShortStrokeName = (stroke) => {
    const normalized = normalizeStroke(stroke).toLowerCase();
    switch (normalized) {
        case 'freestyle': return 'Free';
        case 'butterfly': return 'Fly';
        case 'backstroke': return 'Back';
        case 'breaststroke': return 'Breast';
        case 'individual medley': return 'IM';
        default: return stroke;
    }
};
// An individual medley is always swum in this order, one quarter of the total
// distance per stroke, so a split's stroke can be derived from how far into the
// race it finishes.
export const MEDLEY_LEG_ORDER = ['Butterfly', 'Backstroke', 'Breaststroke', 'Freestyle'];
export const isIndividualMedley = (event) => {
    const stroke = normalizeStroke(parseEventName(event).stroke).toLowerCase();
    return stroke === 'individual medley' || stroke === 'im';
};
// Maps each split's finishing distance to the stroke being swum on that split.
// Returns an empty map when the splits don't line up with the medley legs — a 50m
// split of a 100m IM covers both fly and backstroke, and a half-right stroke label
// is worse than none — so callers can treat "no entries" as "don't annotate".
export const getMedleyLegsByDistance = (event, distances) => {
    const unmapped = new Map();
    if (!isIndividualMedley(event))
        return unmapped;
    const { distance: total } = parseEventName(event);
    if (!Number.isFinite(total) || total % 4 !== 0)
        return unmapped;
    const legLength = total / 4;
    const legs = new Map();
    let previous = 0;
    for (const distance of [...distances].sort((a, b) => a - b)) {
        if (distance <= previous || distance > total)
            return unmapped;
        const leg = Math.ceil(distance / legLength) - 1;
        // The whole (previous, distance] length has to sit inside a single leg.
        if (Math.floor(previous / legLength) !== leg)
            return unmapped;
        legs.set(distance, MEDLEY_LEG_ORDER[leg]);
        previous = distance;
    }
    return legs;
};
export const compareEvents = (aEvent, bEvent) => {
    const a = parseEventName(aEvent);
    const b = parseEventName(bEvent);
    const aStrokePriority = getStrokePriority(a.stroke);
    const bStrokePriority = getStrokePriority(b.stroke);
    if (aStrokePriority !== bStrokePriority) {
        return aStrokePriority - bStrokePriority;
    }
    if (a.distance !== b.distance) {
        return a.distance - b.distance;
    }
    return a.stroke.localeCompare(b.stroke);
};
export const sortStrokeValues = (strokes) => {
    return [...strokes].sort((a, b) => {
        const aPriority = getStrokePriority(a);
        const bPriority = getStrokePriority(b);
        if (aPriority !== bPriority)
            return aPriority - bPriority;
        return a.localeCompare(b);
    });
};
export const sortDistances = (distances) => {
    return [...distances].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
};
// Build the de-duplicated, ordered option lists used by the stroke/distance filter chips.
// Events whose name has no leading distance parse to an infinite distance and are dropped
// from the distance list, since there's no sensible chip to show for them.
export const collectStrokes = (events) => sortStrokeValues(Array.from(new Set(events.map(event => parseEventName(event).stroke))));
export const collectDistances = (events) => sortDistances(Array.from(new Set(events
    .map(event => parseEventName(event).distance)
    .filter(distance => Number.isFinite(distance))
    .map(distance => distance.toString()))));
